import { useState, useEffect } from 'react';
import Papa from 'papaparse';

export interface CsvRow {
  [key: string]: string;
}

export interface CsvData {
  headers: string[];
  rows: CsvRow[];
  totalRows: number;
  constantColumns: {
    headers: string[];
    values: { [key: string]: string };
  };
  variableColumns: {
    headers: string[];
    rows: CsvRow[];
  };
}

const analyzeCsvColumns = (headers: string[], rows: CsvRow[]) => {
  const constantHeaders: string[] = [];
  const variableHeaders: string[] = [];
  const constantValues: { [key: string]: string } = {};

  // Analyze each column
  headers.forEach(header => {
    if (rows.length === 0) {
      variableHeaders.push(header);
      return;
    }

    // Get all unique values for this column
    const uniqueValues = new Set<string>();
    rows.forEach(row => {
      const value = row[header] || '';
      uniqueValues.add(value);
    });

    // If only one unique value, it's constant
    if (uniqueValues.size <= 1) {
      constantHeaders.push(header);
      constantValues[header] = Array.from(uniqueValues)[0] || '';
    } else {
      variableHeaders.push(header);
    }
  });

  // Create variable rows with only variable columns
  const variableRows: CsvRow[] = rows.map(row => {
    const newRow: CsvRow = {};
    variableHeaders.forEach(header => {
      newRow[header] = row[header] || '';
    });
    return newRow;
  });

  return {
    constantColumns: {
      headers: constantHeaders,
      values: constantValues
    },
    variableColumns: {
      headers: variableHeaders,
      rows: variableRows
    }
  };
};

export const useCsvData = (url: string | null) => {
  const [data, setData] = useState<CsvData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCsvData = async (csvUrl: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the full URL directly with the environment variable
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://feed-dane-gov.twopoint.pl';
      const fullUrl = csvUrl.startsWith('http') ? csvUrl : `${apiBaseUrl}${csvUrl}`;
      
      const response = await fetch(fullUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const csvText = await response.text();
      
      // Parse CSV using PapaParse
      const parseResult = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(), // Clean up headers
      });

      if (parseResult.errors.length > 0) {
        console.warn('CSV parsing warnings:', parseResult.errors);
      }

      const headers = parseResult.meta.fields || [];
      const rows = parseResult.data as CsvRow[];

      // Analyze columns to separate constant from variable data
      const { constantColumns, variableColumns } = analyzeCsvColumns(headers, rows);

      setData({
        headers,
        rows,
        totalRows: rows.length,
        constantColumns,
        variableColumns
      });
    } catch (err) {
      console.error('Error fetching CSV data:', err);
      setError(err instanceof Error ? err.message : 'Błąd podczas pobierania danych CSV');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (url) {
      fetchCsvData(url);
    } else {
      setData(null);
    }
  }, [url]);

  return { data, loading, error, refetch: () => url && fetchCsvData(url) };
};